import type { VariantProps } from "class-variance-authority"
import { cva } from "class-variance-authority"

export { default as Button } from "./Button.vue"

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      // FLAT buttons (the theme of 2026-10-10): a fill, a label, a hover that is
      // ONE solid step — never a veil, a shadow or a lit edge. The verb is the
      // mark's peach with an INK label; its hover is the deeper orange. A light
      // button hovers onto the sunk grey, like every other hover in the app.
      // A DISABLED solid button is a dead key: the sunk grey with the second
      // ink, not the fill at half opacity (peach at 50 % is salmon). The link
      // is plain ink that underlines under the cursor — see `link-ink`.
      variant: {
        default:
          // `disabled:border-border` greys a split button's orange divider
          // (`border-l border-primary-hover`) with the rest of the button.
          "bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-sunken disabled:border-border disabled:text-muted-foreground disabled:opacity-100",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-hover disabled:bg-sunken disabled:text-muted-foreground disabled:opacity-100",
        outline:
          // `bg-card`, not stock shadcn's `bg-background`: on a white card the
          // page ground would read as a grey stain.
          "border border-input bg-card hover:bg-sunken",
        secondary: "border border-border bg-sunken text-foreground hover:bg-accent",
        ghost: "hover:bg-sunken",
        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        "default": "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        touch: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
        "icon-sm": "size-9",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
